import { useEffect, useRef } from 'react';
import { useTour } from '@reactour/tour';

/**
 * Hook that automatically skips tour steps if their selectors are not in the DOM
 * Tracks skipped steps to maintain consistent navigation behavior
 */
export const useSkipUnavailableSteps = () => {
  const { currentStep, setCurrentStep, isOpen, steps } = useTour();
  const prevStepRef = useRef(currentStep);
  const skippedStepsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!isOpen || !steps) return;

    const currentStepData = steps[currentStep];
    if (!currentStepData) return;

    // Check if the current step's selector exists in DOM
    const checkSelector = () => {
      if (typeof currentStepData.selector === 'string') {
        const elements = document.querySelectorAll(currentStepData.selector);
        if (elements.length === 0) {
          // Mark this step as skipped
          skippedStepsRef.current.add(currentStep);
          
          // Skip to next step if selector not found
          if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
          }
        } else {
          // Element exists, remove from skipped if it was there
          skippedStepsRef.current.delete(currentStep);
        }
      }
    };

    // If we're moving backward to a previously skipped step, skip it again
    const isMovingBackward = currentStep < prevStepRef.current;
    if (isMovingBackward && skippedStepsRef.current.has(currentStep)) {
      // Continue moving backward to find next available step
      if (currentStep > 0) {
        setCurrentStep(currentStep - 1);
      }
      prevStepRef.current = currentStep;
      return;
    }

    prevStepRef.current = currentStep;

    // Check immediately
    checkSelector();

    // Set up a small delay to allow for dynamic content loading
    const timeout = setTimeout(checkSelector, 100);

    return () => clearTimeout(timeout);
  }, [currentStep, isOpen, steps, setCurrentStep]);
};